import React from 'react'
import { ParkingSpot } from '../../services/mapsService'
import { mapsService } from '../../services/mapsService'
import { useLocalization } from '../../contexts/LocalizationContext'

interface ParkingListProps {
  parkingSpots: ParkingSpot[]
  selectedSpot: ParkingSpot | null
  onSelectSpot: (spot: ParkingSpot) => void
}

export function ParkingList({ parkingSpots, selectedSpot, onSelectSpot }: ParkingListProps) {
  const { t } = useLocalization()
  
  const formatDistance = (distance?: number): string => {
    if (!distance) return ''
    if (distance < 1000) {
      return `${distance}m`
    }
    return `${(distance / 1000).toFixed(1)}km`
  }

  const getPriceLevel = (priceLevel?: number): string => {
    if (!priceLevel) return t('parking.priceNotAvailable')
    const levels = [
      t('parking.priceLevel.free'),
      t('parking.priceLevel.inexpensive'),
      t('parking.priceLevel.moderate'),
      t('parking.priceLevel.expensive'),
      t('parking.priceLevel.veryExpensive')
    ]
    return levels[priceLevel] || t('parking.priceNotAvailable')
  }

  const getRatingStars = (rating?: number): string => {
    if (!rating) return ''
    return '★'.repeat(Math.floor(rating)) + '☆'.repeat(5 - Math.floor(rating))
  }

  const handleNavigate = (spot: ParkingSpot) => {
    mapsService.openDirections(spot)
  }

  if (parkingSpots.length === 0) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-400 mb-4">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('parking.noSpotsFound.title')}</h3>
        <p className="text-gray-600">{t('parking.noSpotsFound.description')}</p>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-5 border-b border-slate-100 bg-white/95 backdrop-blur-md sticky top-0 z-10">
        <h2 className="text-[19px] font-black text-slate-900 tracking-tight">
          {t('parking.nearbyParking')}
        </h2>
      </div>
      
      <div className="space-y-4 p-4">
        {parkingSpots.map((spot) => (
          <div
            key={spot.place_id}
            className={`bg-white rounded-[1.5rem] p-4 cursor-pointer transition-all duration-200 border-2 ${
              selectedSpot?.place_id === spot.place_id
                ? 'border-emerald-500 shadow-lg shadow-emerald-500/10'
                : 'border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200'
            }`}
            onClick={() => onSelectSpot(spot)}
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1 pr-4">
                <h3 className="font-black text-slate-900 text-[17px] mb-1 leading-tight tracking-tight">
                  {spot.name}
                </h3>
                <p className="text-slate-500 text-[13px] font-medium mb-3 line-clamp-1">{spot.address}</p>
                
                <div className="flex items-center space-x-3 text-[13px] font-bold">
                  {spot.distance && (
                    <div className="flex items-center text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md">
                      <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {formatDistance(spot.distance)}
                    </div>
                  )}
                  
                  {spot.rating && (
                    <div className="flex items-center text-amber-500 bg-amber-50 px-2 py-1 rounded-md">
                      <span className="text-[11px] mr-1 mb-[1px]">{getRatingStars(spot.rating)}</span>
                      <span className="text-amber-700">({spot.rating})</span>
                    </div>
                  )}
                </div>
              </div>
              
              {spot.photos && spot.photos.length > 0 && (
                <img
                  src={spot.photos[0]}
                  alt={spot.name}
                  className="w-16 h-16 rounded-xl object-cover shadow-sm border border-slate-100"
                />
              )}
            </div>
            
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center space-x-2 text-[12px] font-bold uppercase tracking-wider">
                <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                  {getPriceLevel(spot.price_level)}
                </span>
                
                {spot.opening_hours && (
                  <span className={`px-2.5 py-1 rounded-full flex items-center ${
                    spot.opening_hours.open_now
                      ? 'bg-blue-50 text-blue-700'
                      : 'bg-red-50 text-red-700'
                  }`}>
                    <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${spot.opening_hours.open_now ? 'bg-blue-500' : 'bg-red-500'}`}></div>
                    {spot.opening_hours.open_now ? t('parking.status.open') : t('parking.status.closed')}
                  </span>
                )}
              </div>
              
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleNavigate(spot)
                }}
                className="bg-emerald-500 text-white font-bold text-[13px] px-4 py-2 rounded-xl shadow-md shadow-emerald-500/20 hover:bg-emerald-600 active:scale-95 transition-all"
              >
                {t('parking.navigate')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
